import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { AppContext } from '../../context/AppContext';
import { Apelacion, Sancion } from '../../models/types';
import { FaSave, FaArrowLeft, FaUpload, FaTrash, FaExclamationTriangle } from 'react-icons/fa';
import { theme } from '../../theme';

// Servicio para apelaciones (se implementaría completamente)
const apelacionServiceFirestore = {
  create: async (apelacion: Omit<Apelacion, 'id'>): Promise<Apelacion | null> => {
    // Implementación real pendiente
    console.log('Creando apelación:', apelacion);
    return {
      id: 'nueva-apelacion-id',
      ...apelacion
    };
  },
  getById: async (id: string): Promise<Apelacion | null> => {
    // Implementación real pendiente
    return null;
  },
  update: async (id: string, apelacion: Partial<Apelacion>): Promise<boolean> => {
    // Implementación real pendiente
    return true;
  },
  delete: async (id: string): Promise<boolean> => {
    // Implementación real pendiente
    return true;
  }
};

// Estilos
const Container = styled.div`
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 30px;
  border-bottom: 2px solid ${theme.colors.primary.main};
  padding-bottom: 15px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #101325;
  font-size: 1.5rem;
  cursor: pointer;
  margin-right: 15px;
  
  &:hover {
    color: #e4312e;
  }
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 2.2rem;
  font-weight: ${theme.typography.fontWeights.bold};
  
  svg {
    color: ${theme.colors.primary.main};
  }
`;

const Form = styled.form`
  background-color: white;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 10px;
  font-weight: ${theme.typography.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  font-size: 1rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 2px rgba(16, 19, 37, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 2px rgba(16, 19, 37, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 2px rgba(16, 19, 37, 0.1);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 30px;
`;

const Button = styled.button`
  background-color: ${theme.colors.primary.main};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 12px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  font-weight: 500;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  font-size: 1rem;
  
  svg {
    margin-right: 8px;
  }
  
  &:hover {
    background-color: ${theme.colors.secondary.main};
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const CancelButton = styled(Button)`
  background-color: ${theme.colors.text.secondary};
  
  &:hover {
    background-color: ${theme.colors.text.primary};
  }
`;

const EvidenceContainer = styled.div`
  margin-top: 15px;
`;

const EvidenceItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px;
  background-color: ${theme.colors.background.default};
  border-radius: 6px;
  margin-bottom: 12px;
  transition: all 0.2s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  }
`;

const EvidenceActions = styled.div`
  display: flex;
  gap: 10px;
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputLabel = styled.label`
  display: inline-flex;
  align-items: center;
  padding: 10px 15px;
  background-color: ${theme.colors.background.default};
  border: 1px solid ${theme.colors.primary.main};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${theme.colors.primary.main};
  font-weight: ${theme.typography.fontWeights.medium};
  
  svg {
    margin-right: 8px;
  }
  
  &:hover {
    background-color: ${theme.colors.primary.main};
    color: white;
  }
`;

const AlertBox = styled.div`
  padding: 16px 20px;
  background-color: rgba(228, 49, 46, 0.05);
  border-left: 4px solid ${theme.colors.secondary.main};
  color: ${theme.colors.text.primary};
  border-radius: 6px;
  margin-bottom: 25px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  line-height: 1.6;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 12px;
    color: ${theme.colors.secondary.main};
    font-size: 1.2rem;
  }
`;

const SuccessBox = styled(AlertBox)`
  background-color: rgba(76, 175, 80, 0.05);
  border-left: 4px solid ${theme.colors.success.main};
  
  svg {
    color: ${theme.colors.success.main};
  }
`;

const ApelacionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data, loadData } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Estado del formulario
  const [formData, setFormData] = useState<{
    sancionId: string;
    motivo: string;
    evidencias: string[];
  }>({
    sancionId: '',
    motivo: '',
    evidencias: []
  });
  
  // Obtener la sanción de la URL si existe
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sancionId = params.get('sancion');
    
    if (sancionId) {
      setFormData(prev => ({
        ...prev,
        sancionId
      }));
    }
  }, [location.search]);
  
  // Manejar cambios en el formulario
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Manejar subida de evidencias
  const handleEvidenceUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      // En una implementación real, aquí se subiría el archivo a un servicio de almacenamiento
      // y se obtendría la URL. Por ahora, simulamos el proceso.
      const file = e.target.files[0];
      const fakeUrl = `https://storage.example.com/${file.name}`;
      
      setFormData(prev => ({
        ...prev,
        evidencias: [...prev.evidencias, fakeUrl]
      }));
    }
  };
  
  // Eliminar evidencia
  const handleRemoveEvidence = (index: number) => {
    setFormData(prev => ({
      ...prev,
      evidencias: prev.evidencias.filter((_, i) => i !== index)
    }));
  };
  
  // Enviar formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    if (!formData.sancionId) {
      setError('Debes seleccionar una sanción para apelar');
      return;
    }
    
    if (!formData.motivo) {
      setError('Debes proporcionar un motivo para la apelación');
      return;
    }
    
    try {
      setLoading(true);
      
      const sancion = data.sanciones.find((s: Sancion) => s.id === formData.sancionId);
      if (!sancion) {
        setError('La sanción seleccionada no existe');
        return;
      }
      
      const nuevaApelacion: Omit<Apelacion, 'id'> = {
        sancionId: formData.sancionId,
        socioId: sancion.socioId,
        fechaApelacion: new Date().toISOString().split('T')[0],
        motivo: formData.motivo,
        evidencias: formData.evidencias,
        estado: 'pendiente'
      };
      
      const resultado = await apelacionServiceFirestore.create(nuevaApelacion);
      
      if (resultado) {
        setSuccess('Apelación enviada correctamente');
        setTimeout(() => {
          loadData();
          navigate('/apelaciones');
        }, 2000);
      } else {
        setError('Error al enviar la apelación');
      }
    } catch (err) {
      console.error('Error al enviar la apelación:', err);
      setError('Error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };
  
  // Obtener las sanciones del piloto actual (en una implementación real, se filtrarían por el usuario autenticado)
  const getSancionesPiloto = () => {
    return data.sanciones;
  };
  
  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/apelaciones')}>
          <FaArrowLeft />
        </BackButton>
        <Title><FaExclamationTriangle /> Nueva Apelación</Title>
      </Header>
      
      <Form onSubmit={handleSubmit}>
        {error && (
          <AlertBox>
            <FaExclamationTriangle />
            {error}
          </AlertBox>
        )}
        
        {success && (
          <SuccessBox>
            <FaExclamationTriangle />
            {success}
          </SuccessBox>
        )}
        
        <FormGroup>
          <Label>Sanción a apelar</Label>
          <Select 
            name="sancionId"
            value={formData.sancionId}
            onChange={handleChange}
            required
          >
            <option value="">Selecciona una sanción</option>
            {getSancionesPiloto().map((sancion: Sancion) => {
              const carrera = data.carreras.find((c: any) => c.id === sancion.carreraId);
              return (
                <option key={sancion.id} value={sancion.id}>
                  {carrera?.nombre || 'Carrera desconocida'} - {sancion.descripcion}
                </option>
              );
            })}
          </Select>
        </FormGroup>
        
        <FormGroup>
          <Label>Motivo de la apelación</Label>
          <TextArea 
            name="motivo"
            value={formData.motivo}
            onChange={handleChange}
            placeholder="Explica detalladamente por qué consideras que la sanción debe ser revisada..."
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label>Evidencias</Label>
          <p>Puedes subir capturas de pantalla, vídeos u otros documentos que respalden tu apelación.</p>
          
          <FileInputLabel>
            <FaUpload /> Subir evidencia
            <FileInput 
              type="file"
              accept="image/*,video/*,.pdf"
              onChange={handleEvidenceUpload}
            />
          </FileInputLabel>
          
          <EvidenceContainer>
            {formData.evidencias.map((evidencia, index) => (
              <EvidenceItem key={index}>
                <div>{evidencia.split('/').pop()}</div>
                <EvidenceActions>
                  <Button type="button" onClick={() => window.open(evidencia, '_blank')}>
                    Ver
                  </Button>
                  <Button 
                    type="button" 
                    onClick={() => handleRemoveEvidence(index)}
                    style={{ backgroundColor: '#e4312e' }}
                  >
                    <FaTrash />
                  </Button>
                </EvidenceActions>
              </EvidenceItem>
            ))}
          </EvidenceContainer>
        </FormGroup>
        
        <ButtonContainer>
          <CancelButton type="button" onClick={() => navigate('/apelaciones')}>
            Cancelar
          </CancelButton>
          <Button type="submit" disabled={loading}>
            <FaSave /> {loading ? 'Enviando...' : 'Enviar Apelación'}
          </Button>
        </ButtonContainer>
      </Form>
    </Container>
  );
};

export default ApelacionDetail;
